import {
  getMaterialInfoPR,
  getMaterialInfoKit,
} from '@/repositories/Materials';
import {
  getBackorders,
  deleteBackorder,
  generateOrder,
} from '@/repositories/Backorders';

import { getUniqueID } from '@/utilities/helpers';
import { Roles } from '@/core/constants/roleConstants';

export default {
  namespaced: true,
  state: {
    reminders: [],
    selectedReminders: [],
    loading: false,
    error: null,
    isDelete: null,
    isDeleteActive: false,
    roles: [
      'Seller',
      'Customer',
      'CustomerPR',
      'Manager',
      'Submanager',
      'RegionalManager',
      'IELExpressSales',
      'CreditAssistant',
      'Subdirector',
      'SubDirectorPRNoCommercial',
      'SubDirectorDTNoCommercial',
      'Director',
      'DirectorNoCommercial',
      'Superuser',
      'IELMarketing',
      Roles.POST_SALE,
    ], // Roles that can use this module
  },

  getters: {
    reminders: (state) => state.reminders,
    remindersCount: (state) => state.reminders.length,
    hasReminders: (state, getters) => getters.remindersCount > 0,
    loadingReminders: (state) => state.loading,
    selectedReminders: (state) => state.selectedReminders,
    hasRemindersSelected: (state) => state.selectedReminders.length > 0,
    isDeleteReminders: (state) => state.isDelete,
    isDeleteActive: (state) => state.isDeleteActive,
    firstReminderSelected: (state, getters) => {
      if (getters.hasRemindersSelected) {
        return state.selectedReminders[0];
      }
      return null;
    },
    firstReminderSelectedIsKit: (state, getters) => {
      if (getters.firstReminderSelected !== null) {
        return getters.firstReminderSelected.isKit;
      }
      return false;
    },
    availableReminders: (getters) => {
      return getters.reminders.filter(
        (r) =>
          reminderHasStock(r.status) && r.backorderProgrammedDisabled == false
      );
    },
    availableRemindersComplete: (getters) => {
      return getters.reminders.filter(
        (r) =>
          reminderHasStockComplete(r.status) &&
          r.backorderProgrammedDisabled == false
      );
    },
    availableRemindersCount: (state, getters) =>
      getters.availableRemindersComplete.length,
    hasAvailableReminders: (state, getters) =>
      getters.availableRemindersCount > 0,
    blockedUser: (state, getters, rootState, rootGetters) => {
      const roles = rootGetters['auth/roles'];
      const isSeller = roles.some((role) => role.name === 'Seller');

      if (isSeller) return false;

      return getters.availableReminders.length > 0;
    },
    isReminderWithErrors: () => (reminder) => {
      return !!reminder?.hasError;
    },
    isReminderCompatible: (state, getters) => (reminder) => {
      return validateCompatibility(getters.firstReminderSelected, reminder);
    },
    isReminderSelectable: (state, getters) => (reminder) => {
      const isReel = reminder.isReel;
      const hasError = reminder.hasError;
      const isProgrammedDisabled = reminder.backorderProgrammedDisabled;
      const hasStock = validateStock(reminder);
      const isCompatible = validateCompatibility(
        getters.firstReminderSelected,
        reminder
      );

      let isSelectable;
      const isDeleteActive = getters.isDeleteActive;

      if (isDeleteActive) {
        isSelectable = true;
      } else {
        isSelectable =
          hasStock &&
          isCompatible &&
          !isReel &&
          !isProgrammedDisabled &&
          !hasError;
      }

      return isSelectable;
    },
    roles: (state) => state.roles,
  },

  mutations: {
    SET_REMINDERS(state, reminders) {
      state.reminders = [...reminders];
    },
    UPDATE_REMINDER(state, reminderUpdated) {
      const reminderIndex = state.reminders.findIndex(
        (r) => r.id === reminderUpdated.id
      );

      if (reminderIndex >= 0)
        state.reminders.splice(reminderIndex, 1, reminderUpdated);
    },
    SET_REMINDERS_SELECTED(state, reminders) {
      state.selectedReminders = [...reminders];
    },
    REMOVE_ITEM_SELCETED(state, reminder) {
      const reminderIndex = state.selectedReminders.findIndex(
        (r) => r.id === reminder.id
      );

      if (reminderIndex >= 0) state.selectedReminders.splice(reminderIndex, 1);
    },

    UDPATE_ITEM_SELCETED(state, reminder) {
      const reminderIndex = state.selectedReminders.findIndex(
        (r) => r.id === reminder.id
      );

      if (reminderIndex >= 0)
        state.selectedReminders.splice(reminderIndex, 1, reminder);
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error = null) {
      state.error = error;
    },
    SET_IS_DELETE_PROCESS(state, data) {
      state.isDelete = data;
    },
    SET_SELECTED_REMINDERS(state, reminders) {
      state.selectedReminders = [...reminders];
    },
    SET_IS_DELETE_ACTIVE(state, isActive) {
      state.isDeleteActive = isActive;
    },
    REMOVE_REMINDERS(state, reminderIds) {
      state.reminders = state.reminders.filter(
        (reminder) => !reminderIds.includes(reminder.id)
      );
    },
    REMOVE_SELECTED_REMINDERS(state, reminderIds) {
      state.selectedReminders = state.selectedReminders.filter(
        (reminder) => !reminderIds.includes(reminder.id)
      );
    },
  },
  actions: {
    async loadReminders({ commit, rootGetters, dispatch, state }) {
      const userId = rootGetters['auth/userCode'];
      const hasRoles = await dispatch('auth/hasAnyRole', state.roles, {
        root: true,
      });

      if (userId && hasRoles) {
        commit('SET_LOADING', true);
        commit('SET_ERROR');

        const roles = rootGetters['auth/roles'][0].name;

        try {
          const reminders = await getBackorders(userId, roles);

          // SETS A UNIQUE ID TO PREVENT VUE FROM CRASHING IF THE ITEM DOESNT HAVE AN ID TO USE AS KEY
          const normalizedReminders = reminders.map((r) => ({
            ...r,
            uniqueId: getUniqueID(),
          }));

          commit('SET_REMINDERS', normalizedReminders);
        } catch (error) {
          commit('SET_ERROR', 'Hubo un error al recuperar los recordatorios');
        }

        commit('SET_LOADING', false);
      } else {
        commit('SET_REMINDERS', []); // If user is null reminders list is cleaned up
        commit('SET_REMINDERS_SELECTED', []);
      }
    },
    async removeReminder({ commit }, { backorders }) {
      await deleteBackorder(backorders.data);
      const idsToRemove = backorders.data.map((backorder) => backorder.id);

      commit('REMOVE_REMINDERS', idsToRemove);
      commit('SET_REMINDERS_SELECTED', []);
    },
    cleanReminders({ commit }) {
      commit('SET_REMINDERS', []);
      commit('SET_REMINDERS_SELECTED', []);
    },
    updateRemindersSelectionList({ commit }, newList) {
      commit('SET_REMINDERS_SELECTED', newList);
    },
    async validateItem({ getters, commit, rootGetters }, reminder) {
      if (
        !matchFirstReminderSelected(getters.firstReminderSelected, reminder)
      ) {
        commit('REMOVE_ITEM_SELCETED', reminder);
      } else {
        const authUser = rootGetters['auth/user'];

        const customer = {
          customer: reminder.customer.code,
          channel: authUser.VTWEG,
          VTWEG: authUser.VTWEG,
          VKBUR: authUser.VKBUR,
          VKORG: authUser.VKORG,
        };

        try {
          if (reminder.isKit) {
            await getMaterialInfoKit(
              reminder.materialId,
              reminder.requiredQty,
              customer
            ); // Just to try catch any
            // commit('UDPATE_ITEM_SELCETED', reminder);
          } else {
            const material = await getMaterialInfoPR(
              reminder.materialId,
              reminder.requiredQty,
              customer
            );
            const newMaterial = {
              ...reminder,
              reminders: material.recordatorios,
              priceList: material.precio_lista,
            };

            commit('UDPATE_ITEM_SELCETED', newMaterial);
          }
        } catch (error) {
          const defaultErrorMessage = 'No se pudo seleccionar el backorder';

          commit('REMOVE_ITEM_SELCETED', reminder);

          reminder.hasError = true;
          commit('UPDATE_REMINDER', reminder);

          if (error?.response?.status == 404) {
            return error.response.data?.error_code || defaultErrorMessage;
          }
          if (error?.response?.status == 400) {
            return error.response.data?.message || defaultErrorMessage;
          } else {
            return defaultErrorMessage;
          }
        }
      }

      return null;
    },

    getUserReminders: ({ getters }, userId) => {
      return getters.reminders.filter((r) => r.user.code == userId);
    },

    getUserAvailableReminders: ({ getters }, userId) => {
      return getters.reminders.filter(
        (r) =>
          r.customer.code == userId &&
          reminderHasStock(r.status) &&
          r.backorderProgrammedDisabled == false
      );
    },

    updateDelete({ commit }, data) {
      commit('SET_IS_DELETE_PROCESS', data);
      return data;
    },

    isDeleteUncheck: ({ commit }, reminders) => {
      const reminder = [];
      for (let i = 0; i < reminders.length; i++) {
        reminder.push(reminders[i]);
      }
      reminder.forEach((item) => {
        commit('REMOVE_ITEM_SELCETED', item);
      });
    },
    updateSelectedReminders({ commit }, reminders) {
      commit('SET_SELECTED_REMINDERS', reminders);
    },
    clearSelectedReminders({ commit }) {
      commit('SET_SELECTED_REMINDERS', []);
    },
    updateIsDeleteActive({ commit }, isActive) {
      commit('SET_IS_DELETE_ACTIVE', isActive);
      return isActive;
    },
    selectOrderGroup({ state, commit }, reminder) {
      if (!reminder) return;

      const isItemAlreadyInStore = state.selectedReminders.some(
        (s) => s.id === reminder.id
      );

      if (isItemAlreadyInStore) {
        const remaining = state.selectedReminders.filter(
          (s) => s.order !== reminder.order
        );
        commit('SET_SELECTED_REMINDERS', remaining);
        return;
      }

      // Auto-select only diferent to 'GRIS' items from the same order
      // isReminderSelectable (no reel, no error, no disabled)
      const autoSelected = state.reminders.filter(
        (r) =>
          r.order === reminder.order &&
          r.status !== 'GRIS' &&
          !r.isReel &&
          !r.hasError &&
          !r.backorderProgrammedDisabled
      );

      const newSelection = [...state.selectedReminders];
      autoSelected.forEach((item) => {
        if (!newSelection.some((s) => s.id === item.id)) {
          newSelection.push(item);
        }
      });

      commit('SET_SELECTED_REMINDERS', newSelection);
    },
    async generateBackorder({ commit, dispatch, state }, { backorders }) {
      const response = await generateOrder(backorders.backorderData);

      const processedIds = backorders.backorderData.map(
        (backorder) => backorder.id
      );

      const processedReminders = state.reminders.filter((r) =>
        processedIds.includes(r.id)
      );

      const idsToRemove = processedReminders
        .filter((r) => r.status !== 'AMARILLO')
        .map((r) => r.id);

      const idsToRefresh = processedReminders
        .filter((r) => r.status === 'AMARILLO')
        .map((r) => r.id);

      //delete the completed ones in green
      if (idsToRemove.length) {
        commit('REMOVE_REMINDERS', idsToRemove);
      }

      // only refresh the partial rows, without removing the row.
      if (idsToRefresh.length) {
        await dispatch('refreshPartialReminders', idsToRefresh);
      }

      commit('SET_REMINDERS_SELECTED', []);
      return response;
    },

    async refreshPartialReminders({ commit, rootGetters }, ids) {
      const userId = rootGetters['auth/userCode'];
      const roles = rootGetters['auth/roles'][0].name;

      try {
        const freshReminders = await getBackorders(userId, roles);

        // only update the partial rows that we process, not the entire table.
        ids.forEach((id) => {
          const updated = freshReminders.find((r) => r.id === id);
          if (updated) {
            const normalized = { ...updated, uniqueId: getUniqueID() };
            commit('UPDATE_REMINDER', normalized);
          }
        });
      } catch (error) {
        commit(
          'SET_ERROR',
          'No se pudo actualizar la información de partidas parciales'
        );
      }
    },
  },
};

const reminderHasStock = (status) => {
  return status === 'VERDE';
};

const reminderHasStockComplete = (status) => {
  return status === 'AMARILLO' || status === 'VERDE';
};

const matchFirstReminderSelected = (firstSelectedReminder, newReminder) => {
  if (!firstSelectedReminder) return true;
  return firstSelectedReminder.order === newReminder.order;

  //return false;
};

const validateCompatibility = (firstSelected, reminder) => {
  if (!firstSelected) return true;

  // kit validation
  if (firstSelected.isKit !== reminder.isKit) return false;

  // validate same order
  return firstSelected.order === reminder.order;
};

const validateStock = (reminder) => {
  const status = reminder.status;
  return status === 'VERDE' || status === 'AMARILLO';
};
